'use client';

import _ from 'lodash';

import { haflaOpsCallAndWatiNumber, ORDER_LIST_INDEX } from 'config/common';
import { ORDER_ACKNOWLEDGEMENT_STATUS } from 'config/searchkit/orderList';
import { DATE_RANGE_ITEMS_FOR_EVENT_DATE } from 'helpers/searchkit';
import { PARTNER_PORTAL_URL } from 'services/connections.service';
import * as HttpService from 'services/http.service';
import {
  LOCAL_STORAGE_KEYS,
  setLocalStorageValue
} from 'services/localStorage.service';
import { getPageURL, PAGE_NAME } from 'services/partnerPortal.service';
import { GET_PARTNER_SUMMARY_STATS_API_URL } from 'services/url.service';

const isProduction = process.env.NEXT_PUBLIC_APP_ENV === 'production';

export const getPartnerSummaryStatsAPI = () =>
  HttpService.getWithAuth(GET_PARTNER_SUMMARY_STATS_API_URL());

export const PARTNER_TIER = {
  BRONZE: {
    label: 'Bronze',
    icon: 'bronze-icon.svg',
    value: 'BRONZE',
    starIcon: 'bronze-star.svg',
    valueStyle: 'text-royal-orange'
  },
  GOLD: {
    label: 'Gold',
    icon: 'gold.svg',
    value: 'GOLD',
    starIcon: 'gold-star.svg',
    valueStyle: 'text-misty-moss'
  },
  SILVER: {
    label: 'Silver',
    icon: 'silver.svg',
    value: 'SILVER',
    starIcon: 'silver-star.svg',
    valueStyle: 'text-light-silver'
  }
};

const getPartnerTier = (orderServedTillDateValue) => {
  const isSilver = (value) => value > 75000 && value < 250000;
  const isGold = (value) => value >= 250000;

  switch (true) {
    case isGold(orderServedTillDateValue):
      return PARTNER_TIER.GOLD.value;

    case isSilver(orderServedTillDateValue):
      return PARTNER_TIER.SILVER.value;

    default:
      return PARTNER_TIER.BRONZE.value;
  }
};

export const parsePartnerSummaryStats = ({ entity }) => {
  const {
    newQuoteCount,
    ordersServedTillDateCount,
    ordersServedTillDateValue,
    ordersToServeTodayTomorrowCount,
    responseRate,
    totalInquiriesReceived,
    upcomingEventOrderAcceptedCount,
    upcomingEventOrderAckPendingCount,
    productsCount
  } = entity;

  const menuItemList = [
    {
      className: 'text-bluish-cyan',
      count: `${newQuoteCount} New Quotes`,
      href: getPageURL({ pageName: PAGE_NAME.BID_LIST.label }),
      icon: 'quotes.svg',
      label: 'Quotes',
      shortDescription:
        'List of quote request for product availability and pricing.',
      sortOrder: 3
    },
    {
      className: 'expired-badge',
      count: `${upcomingEventOrderAckPendingCount} Acknowledgement Pending`,
      href: getPageURL({
        pageName: PAGE_NAME.ORDER_LIST.label,
        queryParams: {
          [`${ORDER_LIST_INDEX}[numericMenu][eventDateInSeconds]`]: `${DATE_RANGE_ITEMS_FOR_EVENT_DATE.UPCOMING.start}:`,
          [`${ORDER_LIST_INDEX}[menu][acknowledgementStatus]`]:
            ORDER_ACKNOWLEDGEMENT_STATUS.PENDING.value
        }
      }),
      icon: 'partner-order-icon.svg',
      label: 'Orders Pending Response',
      onClick: () =>
        setLocalStorageValue({
          key: LOCAL_STORAGE_KEYS.DATE_PICKER_VALUE,
          value: encodeURIComponent(
            JSON.stringify({
              start: DATE_RANGE_ITEMS_FOR_EVENT_DATE.UPCOMING.start
            })
          ).replace('%3A', ':')
        }),
      shortDescription: 'List of all upcoming orders pending acknowledgement.',
      sortOrder: 1
    },
    {
      className: 'submitted-badge',
      count: `${upcomingEventOrderAcceptedCount} Upcoming Orders`,
      href: getPageURL({
        pageName: PAGE_NAME.ORDER_LIST.label,
        queryParams: {
          [`${ORDER_LIST_INDEX}[numericMenu][eventDateInSeconds]`]: `${DATE_RANGE_ITEMS_FOR_EVENT_DATE.UPCOMING.start}:`,
          [`${ORDER_LIST_INDEX}[menu][acknowledgementStatus]`]:
            ORDER_ACKNOWLEDGEMENT_STATUS.ACCEPT_ORDER.value
        }
      }),
      icon: 'orders-accepted.svg',
      label: 'Orders Accepted',
      onClick: () =>
        setLocalStorageValue({
          key: LOCAL_STORAGE_KEYS.DATE_PICKER_VALUE,
          value: encodeURIComponent(
            JSON.stringify({
              start: DATE_RANGE_ITEMS_FOR_EVENT_DATE.UPCOMING.start
            })
          ).replace('%3A', ':')
        }),
      shortDescription: 'List of all upcoming accepted orders.',
      sortOrder: 2
    },
    {
      className: 'in-progress-badge',
      count: `${ordersToServeTodayTomorrowCount} Deliveries and Pickups`,
      href: getPageURL({
        pageName: PAGE_NAME.ORDER_DELIVERY_AND_PICKUPS.label
      }),
      icon: 'delivery-and-pickup-v1.svg',
      label: 'Deliveries & Pickups',
      shortDescription:
        'List of all orders to be delivered or picked-up today or tomorrow',
      sortOrder: 0
    },
    {
      className: 'expired-badge',
      count: `${productsCount} Product(s)`,
      href: getPageURL({
        pageName: PAGE_NAME.PRODUCTS.label
      }),
      icon: 'my-catalogue.svg',
      label: 'My Catalogue',
      shortDescription: `List of all my products uploaded on the Hafla's system`,
      sortOrder: 4
    }
  ]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((a) => _.omit(a, 'sortOrder'));

  const statsArray = [
    {
      count: totalInquiriesReceived,
      countLabel: 'Inquiries',
      heading: 'Quote Requests',
      iconUrl: 'quote-check-icon.svg',
      value: `${responseRate ?? 0}%`,
      valueLabel: 'Submission Rate'
    },
    {
      count: ordersServedTillDateCount,
      countLabel: 'Orders',
      heading: 'Business till date',
      iconUrl: 'stats-icon.svg',
      value: ordersServedTillDateValue.toLocaleString('en-IN', {
        maximumFractionDigits: 0
      }),
      valueLabel: 'Order Value'
    }
  ];
  return {
    menuItemList,
    partnerTier: getPartnerTier(ordersServedTillDateValue),
    statsArray
  };
};

export const fetchPartnerSummaryStats = async ({
  setLoading,
  setPartnerSummaryStats,
  setShowToast
}) => {
  try {
    setLoading(true);
    const { entity, message, status } = await getPartnerSummaryStatsAPI();
    if (status) {
      setPartnerSummaryStats(parsePartnerSummaryStats({ entity }));
    } else {
      setShowToast({ message, show: true, successToast: status });
    }
  } catch (error) {
    setShowToast({
      message: error.message || 'Failed to get Partner stats',
      show: true,
      successToast: false
    });
  } finally {
    setLoading(false);
  }
};

export const getInitiateWhatsappChatLink = () => {
  if (typeof window !== 'undefined') {
    const showFullURL = true;
    const { pathname, searchParams } = new URL(window.location.href);
    const pageURLWithoutQueryParam = `${PARTNER_PORTAL_URL}${pathname}`;

    const searchString = searchParams.toString();
    const encodedQueryString =
      searchString.length > 0 ? `, ${encodeURIComponent(searchString)}` : '';

    const messagePrefix = isProduction
      ? ''
      : `Please Ignore this - this message is from the non production portal. \n\n`;

    const whatsAppBaseURL = 'https://api.whatsapp.com/send';

    const fullURL = showFullURL
      ? `\n\n${pageURLWithoutQueryParam}${encodedQueryString}\n\n`
      : '';

    const formattedMessage = `${messagePrefix}Hi Hafla! I have a query on - ${pageURLWithoutQueryParam}\n\nPlease get in touch.${fullURL}`;

    return `${whatsAppBaseURL}?phone=${haflaOpsCallAndWatiNumber}&text=${encodeURIComponent(formattedMessage)}`;
  }

  return '#';
};
